import { api, apiBytes } from "./client";

/*
 * Settings > Customization and Automation, and the per-record extras (custom fields, tags, files).
 * Server: server/customize.js. Amounts the server sends are rupee strings; nothing here does money arithmetic.
 */

const get = <T,>(path: string, query?: Record<string, string | undefined>) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) if (v) qs.set(k, v);
  const s = qs.toString();
  return api<T>("GET", s ? `${path}?${s}` : path);
};
const post = <T,>(path: string, body: unknown = {}) => api<T>("POST", path, body);
const patch = <T,>(path: string, body: unknown) => api<T>("PATCH", path, body);
const put = <T,>(path: string, body: unknown) => api<T>("PUT", path, body);
const del = <T = void,>(path: string) => api<T>("DELETE", path);

/* ---------- number series ---------- */

export type NumberSeries = {
  kind: string;
  label: string;
  pattern: string;
  padding: number;
  reset: "fy" | "never";
  period: string | null;
  last_seq: number;
  next_seq: number;
  next_number: string;
  last_issued: string | null;
  is_default: boolean;
  reset_locked: boolean;
};
export type NumberSeriesIn = { pattern?: string; padding?: number; reset?: "fy" | "never"; next_seq?: number | null };

/* ---------- custom fields ---------- */

export type FieldType = "text" | "number" | "date" | "dropdown" | "checkbox" | "email" | "url";
export type FieldModule = "customers" | "items" | "invoices" | "subscriptions" | "vendors" | "bills" | "expenses" | "credit_notes";
export type CustomField = {
  id: string;
  module: FieldModule;
  label: string;
  type: FieldType;
  options: string[];
  is_required: boolean;
  is_unique: boolean;
  show_in_pdf: boolean;
  show_in_portal: boolean;
  status: "active" | "inactive";
  /** Given to every new record that comes without a value, including the ones the CRM makes itself. */
  default_value?: string | number | boolean | null;
  used: number;
};
export type CustomFieldIn = Partial<Pick<CustomField, "label" | "type" | "options" | "is_required" | "is_unique" | "show_in_pdf" | "show_in_portal" | "status">> & { module?: FieldModule; default_value?: string | null };
export type FieldValue = string | number | boolean | null;
/** Required fields a record the CRM made itself (a conversion, a recurring run, an import) was left without; gone once filled. */
export type CustomFieldHold = { module: string; record_id: string; source: string; since: string; missing: string[]; reason: string };
export type RecordFields = { module: string; record_id: string | null; fields: (CustomField & { value: FieldValue })[]; hold?: CustomFieldHold | null };
/** What a record's create (and edit) body may carry: custom field id -> value, checked and saved with the record. */
export type CustomFieldsIn = { custom_fields?: Record<string, FieldValue> };

/* ---------- reporting tags ---------- */

export type TagOption = { id: string; value: string; status: "active" | "inactive"; used: number };
export type ReportingTag = { id: string; name: string; description: string | null; modules: string[]; is_required: boolean; status: "active" | "inactive"; options: TagOption[] };
export type RecordTags = { module: string; record_id: string; tags: { tag_id: string; name: string; is_required: boolean; options: { id: string; value: string }[]; selected: string | null }[] };

/* ---------- templates ---------- */

export type EmailTemplate = { key: string; name: string; builtin: boolean; enabled: boolean; subject: string; body: string; last_error: string | null; vars: string[]; updated_at: string; updated_by: string | null };
export type Placeholder = { key: string; meaning: string; sample: string };
export type Preview = { ok: true; subject: string; body_html: string; text: string } | { ok: false; error: string };
export type PrintSettings = { layout: "classic" | "modern" | "compact"; accent_color: string; text_color: string; footer_text: string; show_logo: boolean; logo: string | null; paper: "A4" | "Letter" };

/* ---------- workflow rules ---------- */

export type RuleCondition = { field: string; op: string; value: string };
export type RuleAction =
  | { type: "send_email"; template: string; to: string }
  | { type: "create_note"; message: string }
  | { type: "create_task"; title: string; due_in_days: number }
  | { type: "call_webhook"; webhook_id: string }
  | { type: "update_field"; field_id: string; value: FieldValue }
  | { type: "send_sms"; template_id: string; to: string }
  | { type: "send_whatsapp"; template_id: string; to: "client" };
export type WorkflowRule = { id: string; name: string; event: string; event_label: string; conditions: RuleCondition[]; actions: RuleAction[]; enabled: boolean; created_by: string | null; created_at: string; last_run: { status: string; created_at: string } | null; runs: number; failed: number };
export type RuleCatalog = {
  events: { key: string; label: string; fields: string[]; record_module: string | null }[];
  condition_fields: { key: string; label: string; type: "number" | "text" | "tag"; ops: string[] }[];
  action_types: { key: string; label: string }[];
  rules: WorkflowRule[];
};
export type RuleRun = { id: string; rule_id: string | null; rule_name: string; event: string; event_label: string; status: "pending" | "retrying" | "success" | "failed"; error: string | null; attempts: number; next_retry_at: string | null; created_at: string; finished_at: string | null; results: { type: string; ok: boolean; detail: string }[] };
export type FollowUp = { id: string; title: string; due_date: string; status: "open" | "done"; lead_id: string | null; lead_name: string | null; created_at: string };

/* ---------- webhooks ---------- */

export type WebhookEndpoint = { id: string; name: string; url: string; events: string[]; enabled: boolean; created_at: string; created_by: string | null; last_delivery: { status: string; last_code: number | null; last_error: string | null; created_at: string } | null; delivered: number; failed: number; pending: number; secret?: string };
export type WebhookDelivery = { id: string; endpoint_id: string; event: string; status: "pending" | "success" | "failed"; attempts: number; next_attempt_at: string | null; last_code: number | null; last_error: string | null; created_at: string; delivered_at: string | null };
export type IncomingHook = { id: string; name: string; path: string; enabled: boolean; created_at: string; created_by: string | null; last_received_at: string | null; received_count: number; secret?: string };
export type IncomingCall = { id: string; received_at: string; ok: boolean; detail: string | null; size: number | null };

/* ---------- attachments ---------- */

export type AttachEntity = "customers" | "invoices" | "bills" | "expenses";
export type Attachment = { id: string; entity: AttachEntity; record_id: string; name: string; content_type: string; size: number; uploaded_by: string | null; uploaded_at: string };

export const customize = {
  numberSeries: () => get<NumberSeries[]>("/admin/customize/number-series"),
  saveSeries: (kind: string, b: NumberSeriesIn) => post<NumberSeries>(`/admin/customize/number-series/${kind}`, b),
  defaultSeries: (kind: string) => post<NumberSeries>(`/admin/customize/number-series/${kind}/default`),

  fields: (module?: string) => get<{ modules: FieldModule[]; types: FieldType[]; fields: CustomField[] }>("/admin/customize/custom-fields", { module }),
  createField: (b: CustomFieldIn) => post<CustomField>("/admin/customize/custom-fields", b),
  updateField: (id: string, b: CustomFieldIn) => patch<CustomField>(`/admin/customize/custom-fields/${id}`, b),
  deleteField: (id: string) => del(`/admin/customize/custom-fields/${id}`),

  tags: () => get<{ modules: string[]; tags: ReportingTag[] }>("/admin/customize/tags"),
  createTag: (b: { name: string; description?: string | null; modules: string[]; is_required: boolean; options: string[] }) => post<ReportingTag>("/admin/customize/tags", b),
  updateTag: (id: string, b: Partial<{ name: string; description: string | null; modules: string[]; is_required: boolean; status: "active" | "inactive" }>) => patch<ReportingTag>(`/admin/customize/tags/${id}`, b),
  deleteTag: (id: string) => del(`/admin/customize/tags/${id}`),
  addTagOptions: (id: string, values: string[]) => post<ReportingTag>(`/admin/customize/tags/${id}/options`, { values }),
  updateTagOption: (id: string, oid: string, b: { value?: string; status?: "active" | "inactive" }) => patch<ReportingTag>(`/admin/customize/tags/${id}/options/${oid}`, b),
  deleteTagOption: (id: string, oid: string) => del<ReportingTag>(`/admin/customize/tags/${id}/options/${oid}`),
  tagged: (module: string, tag_id: string, option_id?: string) => get<{ record_id: string; label: string | null }[]>("/admin/ext/tagged", { module, tag_id, option_id }),

  templates: () => get<{ placeholders: Placeholder[]; templates: EmailTemplate[] }>("/admin/customize/templates"),
  createTemplate: (b: { name: string; subject: string; body: string }) => post<EmailTemplate>("/admin/customize/templates", b),
  saveTemplate: (key: string, b: Partial<Pick<EmailTemplate, "subject" | "body" | "enabled">>) => patch<EmailTemplate>(`/admin/customize/templates/${key}`, b),
  resetTemplate: (key: string) => post<EmailTemplate>(`/admin/customize/templates/${key}/reset`),
  deleteTemplate: (key: string) => del(`/admin/customize/templates/${key}`),
  previewTemplate: (key: string, b: { subject: string; body: string }) => post<Preview>(`/admin/customize/templates/${key}/preview`, b),
  testTemplate: (key: string, b: { to: string; subject: string; body: string }) => post<{ status: "sent" | "saved"; detail: string | null; to: string }>(`/admin/customize/templates/${key}/test`, b),
  printSettings: () => get<PrintSettings>("/admin/customize/print-settings"),
  savePrintSettings: (b: Partial<PrintSettings>) => post<PrintSettings>("/admin/customize/print-settings", b),

  rules: () => get<RuleCatalog>("/admin/customize/automation/rules"),
  createRule: (b: Pick<WorkflowRule, "name" | "event" | "conditions" | "actions">) => post<WorkflowRule>("/admin/customize/automation/rules", b),
  updateRule: (id: string, b: Partial<Pick<WorkflowRule, "name" | "event" | "conditions" | "actions" | "enabled">>) => patch<WorkflowRule>(`/admin/customize/automation/rules/${id}`, b),
  deleteRule: (id: string) => del(`/admin/customize/automation/rules/${id}`),
  runs: (q: { rule?: string; status?: string }) => get<RuleRun[]>("/admin/customize/automation/runs", q),
  retryRun: (id: string) => post<RuleRun>(`/admin/customize/automation/runs/${id}/retry`),
  tasks: (status: "open" | "done") => get<FollowUp[]>("/admin/customize/automation/tasks", { status }),
  completeTask: (id: string) => post<void>(`/admin/customize/automation/tasks/${id}/done`),
  scan: () => post<{ ok: boolean }>("/admin/customize/automation/scan"),

  webhooks: () => get<{ events: { key: string; label: string }[]; endpoints: WebhookEndpoint[] }>("/admin/customize/webhooks"),
  createWebhook: (b: { name: string; url: string; events: string[] }) => post<WebhookEndpoint>("/admin/customize/webhooks", b),
  updateWebhook: (id: string, b: Partial<{ name: string; url: string; events: string[]; enabled: boolean }>) => patch<WebhookEndpoint>(`/admin/customize/webhooks/${id}`, b),
  deleteWebhook: (id: string) => del(`/admin/customize/webhooks/${id}`),
  rotateWebhook: (id: string) => post<WebhookEndpoint>(`/admin/customize/webhooks/${id}/rotate-secret`),
  testWebhook: (id: string) => post<{ delivery_id: string }>(`/admin/customize/webhooks/${id}/test`),
  deliveries: (endpoint?: string) => get<WebhookDelivery[]>("/admin/customize/webhooks/deliveries", { endpoint }),
  retryDelivery: (id: string) => post<WebhookDelivery>(`/admin/customize/webhooks/deliveries/${id}/retry`),
  incoming: () => get<IncomingHook[]>("/admin/customize/webhooks/incoming"),
  createIncoming: (name: string) => post<IncomingHook>("/admin/customize/webhooks/incoming", { name }),
  updateIncoming: (id: string, b: Partial<{ name: string; enabled: boolean }>) => patch<IncomingHook>(`/admin/customize/webhooks/incoming/${id}`, b),
  deleteIncoming: (id: string) => del(`/admin/customize/webhooks/incoming/${id}`),
  rotateIncoming: (id: string) => post<IncomingHook>(`/admin/customize/webhooks/incoming/${id}/rotate-secret`),
  incomingCalls: (id: string) => get<IncomingCall[]>(`/admin/customize/webhooks/incoming/${id}/calls`),
};

/** What a record's screen reads and saves: custom field values, reporting tags and files. */
export const recordExtras = {
  values: (module: string, record_id: string) => get<RecordFields>("/admin/ext/custom-values", { module, record_id }),
  /** The module's active fields, for a create form (no record yet). */
  fields: (module: string) => get<RecordFields>("/admin/ext/custom-values", { module }),
  saveValues: (module: string, record_id: string, values: Record<string, FieldValue>) => put<RecordFields>(`/admin/ext/custom-values?module=${module}&record_id=${encodeURIComponent(record_id)}`, { values }),
  tags: (module: string, record_id: string) => get<RecordTags>("/admin/ext/tags", { module, record_id }),
  saveTags: (module: string, record_id: string, assignments: Record<string, string | null>) => put<RecordTags>(`/admin/ext/tags?module=${module}&record_id=${encodeURIComponent(record_id)}`, { assignments }),
  nextNumber: (kind: string) => get<{ number: string }>("/admin/ext/next-number", { kind }),

  attachments: (entity: AttachEntity, record_id: string) => get<Attachment[]>("/admin/ext/attachments", { entity, record_id }),
  /** `name` for a Blob (a photo kept by the offline outbox); `extra` adds headers (its Idempotency-Key). */
  upload: async (entity: AttachEntity, record_id: string, file: Blob, name = (file as File).name, extra: Record<string, string> = {}) => {
    const res = await apiBytes("POST", `/admin/ext/attachments?entity=${entity}&record_id=${encodeURIComponent(record_id)}&name=${encodeURIComponent(name)}`, file, { "Content-Type": "application/octet-stream", ...extra });
    return (await res.json()) as Attachment;
  },
  download: async (a: Attachment) => {
    const res = await apiBytes("GET", `/admin/ext/attachments/${a.id}/download`);
    const url = URL.createObjectURL(await res.blob());
    const link = document.createElement("a");
    link.href = url;
    link.download = a.name;
    link.click();
    URL.revokeObjectURL(url);
  },
  deleteAttachment: (id: string) => del(`/admin/ext/attachments/${id}`),
};
